import contentImageManifest from '../data/content-image-manifest.json';

type CloudflareImageOptions = {
  width: number;
  quality: number;
  format?: 'auto';
};

type ContentImageManifestEntry = {
  w: number;
  h: number;
  v: number[];
};

const CLOUDFLARE_IMAGE_PREFIX = '/cdn-cgi/image/';
const ABSOLUTE_URL = /^[a-z][a-z\d+\-.]*:/i;
const ENABLE_CLOUDFLARE_IMAGE_RESIZING = import.meta.env.VITE_ENABLE_CF_IMAGE_RESIZING === 'true';
const INLINE_HTML_IMAGE_DIMENSIONS: Record<string, { width: number; height: number }> = {
  '/images/conditions/divider1.png': { width: 1200, height: 60 },
  '/images/conditions/divider1.webp': { width: 1200, height: 60 },
};
const CONTENT_IMAGE_MANIFEST: Record<string, ContentImageManifestEntry> = contentImageManifest;
const CONTENT_IMAGE_SIZES = '(max-width: 850px) 100vw, 800px';

export function cfImage(src: string, options: CloudflareImageOptions): string {
  if (!src || import.meta.env.DEV || !ENABLE_CLOUDFLARE_IMAGE_RESIZING) return src;
  if (
    src.startsWith(CLOUDFLARE_IMAGE_PREFIX) ||
    src.startsWith('data:') ||
    src.startsWith('blob:') ||
    ABSOLUTE_URL.test(src)
  ) {
    return src;
  }

  const normalizedSrc = src.startsWith('/') ? src : `/${src}`;
  const imageOptions = [
    `width=${options.width}`,
    `format=${options.format ?? 'auto'}`,
    `quality=${options.quality}`,
  ].join(',');

  return `${CLOUDFLARE_IMAGE_PREFIX}${imageOptions}${normalizedSrc}`;
}

export const cfHeroImage = (src: string) => cfImage(src, { width: 1600, format: 'auto', quality: 82 });
export const cfHeadshotImage = (src: string) => cfImage(src, { width: 480, format: 'auto', quality: 85 });
export const cfCardImage = (src: string) => cfImage(src, { width: 640, format: 'auto', quality: 82 });
export const cfInlineImage = (src: string) => cfImage(src, { width: 960, format: 'auto', quality: 82 });

function addMissingAttribute(tag: string, attribute: string, value: string | number): string {
  if (new RegExp(`\\b${attribute}\\s*=`, 'i').test(tag)) {
    return tag;
  }

  return tag.replace(/\s*\/?>$/, (closing) => ` ${attribute}="${value}"${closing}`);
}

function addKnownHtmlImageDimensions(html: string): string {
  return html.replace(/<img\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/gi, (tag, src: string) => {
    const dimensions = INLINE_HTML_IMAGE_DIMENSIONS[src];
    if (!dimensions) {
      return tag;
    }

    return addMissingAttribute(
      addMissingAttribute(tag, 'width', dimensions.width),
      'height',
      dimensions.height
    );
  });
}

function getTagSrc(tag: string): string | undefined {
  return tag.match(/\bsrc=(["'])([^"']*)\1/i)?.[2];
}

// Mirrors the deterministic naming scripts/optimize-content-images.mjs uses
// when it writes variants next to the original file: /images/blog/foo.jpg
// width 800 -> /images/blog/foo-w800.webp.
function contentImageVariantSrc(src: string, width: number, format: 'avif' | 'webp'): string {
  const lastSlash = src.lastIndexOf('/');
  const dir = src.slice(0, lastSlash);
  const filename = src.slice(lastSlash + 1);
  const dotIndex = filename.lastIndexOf('.');
  const base = dotIndex === -1 ? filename : filename.slice(0, dotIndex);
  return `${dir}/${base}-w${width}.${format}`;
}

function buildContentImageSrcset(src: string, widths: number[], format: 'avif' | 'webp'): string {
  return widths.map((width) => `${contentImageVariantSrc(src, width, format)} ${width}w`).join(', ');
}

// sanitizeScrapedHtml rewrites scraped png/jpg srcs to their .webp siblings
// before this transform runs, while the manifest (and the variant files) are
// keyed by the original extension, so a .webp src falls back to its original
// sibling key. The variant filenames only depend on the shared basename, so
// srcsets built from the .webp src still point at the right files.
function lookupManifestEntry(src: string): ContentImageManifestEntry | undefined {
  const direct = CONTENT_IMAGE_MANIFEST[src];
  if (direct || !src.endsWith('.webp')) return direct;
  const base = src.slice(0, -'.webp'.length);
  return (
    CONTENT_IMAGE_MANIFEST[`${base}.jpg`] ??
    CONTENT_IMAGE_MANIFEST[`${base}.jpeg`] ??
    CONTENT_IMAGE_MANIFEST[`${base}.png`]
  );
}

// Adds width/height (CLS protection) and, for images with generated
// responsive variants, wraps the <img> in a <picture> with AVIF/WebP
// srcset sources. Pure deterministic string transform: running it twice on
// its own output is a no-op, so the same html is produced on prerender and
// on the client remount. Images already inside a <picture> (i.e. output
// from a previous pass) are left untouched.
function applyContentImageManifest(html: string): string {
  let imgIndex = 0;

  return html.replace(/<picture\b[\s\S]*?<\/picture>|<img\b[^>]*>/gi, (match) => {
    if (/^<picture\b/i.test(match)) {
      imgIndex += (match.match(/<img\b/gi) ?? []).length;
      return match;
    }

    const isFirstImage = imgIndex === 0;
    imgIndex += 1;

    const src = getTagSrc(match);
    const entry = src ? lookupManifestEntry(src) : undefined;
    if (!src || !entry) {
      return match;
    }

    let imgTag = addMissingAttribute(addMissingAttribute(match, 'width', entry.w), 'height', entry.h);

    if (!isFirstImage) {
      imgTag = addMissingAttribute(addMissingAttribute(imgTag, 'loading', 'lazy'), 'decoding', 'async');
    }

    if (entry.v.length === 0) {
      return imgTag;
    }

    const avifSrcset = buildContentImageSrcset(src, entry.v, 'avif');
    const webpSrcset = buildContentImageSrcset(src, entry.v, 'webp');

    return (
      '<picture>' +
      `<source type="image/avif" srcset="${avifSrcset}" sizes="${CONTENT_IMAGE_SIZES}" />` +
      `<source type="image/webp" srcset="${webpSrcset}" sizes="${CONTENT_IMAGE_SIZES}" />` +
      `${imgTag}</picture>`
    );
  });
}

export function cfInlineHtmlImages(html: string): string {
  const htmlWithDimensions = addKnownHtmlImageDimensions(html);
  const htmlWithResponsiveImages = applyContentImageManifest(htmlWithDimensions);

  if (import.meta.env.DEV || !ENABLE_CLOUDFLARE_IMAGE_RESIZING) return htmlWithResponsiveImages;

  return htmlWithResponsiveImages.replace(
    /(<img\b[^>]*\bsrc=")(\/images\/[^"]+)(")/g,
    (_match, prefix: string, src: string, suffix: string) => `${prefix}${cfInlineImage(src)}${suffix}`
  );
}
